import { Box, CircularProgress, Typography } from '@mui/material';
import { Edit, SimpleForm, useGetOne, useNotify, usePermissions, useRefresh, useTranslate } from 'react-admin';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import CustomCloseButton from '@/components/global/buttons/CustomCloseButton.jsx';
import TagCategoryToolbar from '@/components/global/tags/TagCategoryToolbar';
import { AdvertisementFormContent } from './AdvertisementFormContent.jsx';
import PropTypes from 'prop-types';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import { transformAdvertisement } from '@/helpers/marketing/transformAdvertisement.js';
import { validateAdvertisementForm } from '@/helpers/marketing/validateAdvertisement.js';
import { mapAdvertisementToFormValues } from '@/helpers/marketing/mapAdvertisementToFormValues.js';
import { useCampaignTileImage } from '@/composables/useCampaignTileImage.js';
import { useFormValueSetter } from '@/composables/useFormValueSetter';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints.js';

const StyledAdvertisementEdit = styled(Box)(() => ({
    '& .form-content': {
        '& .range-picker-label': {
            backgroundColor: Palette.white,
        },
        '& .has-preview': {
            minWidth: '600px',
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                minWidth: '100%',
            },
        },
        '& .MuiFormHelperText-root': {
            display: 'none',
            '&.Mui-error': {
                display: 'block',
            },
        },
    },
}));

const AdvertisementExistingImageSync = ({ imageUrl }) => {
    const setFormValue = useFormValueSetter();

    useEffect(() => {
        if (imageUrl) {
            setFormValue('advertisementImage', { src: imageUrl });
        }
    }, [imageUrl, setFormValue]);

    return null;
};

AdvertisementExistingImageSync.propTypes = {
    imageUrl: PropTypes.string,
};

export const AdvertisementEdit = ({ handleClose = () => {}, elementId }) => {
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const queryClient = useQueryClient();
    const { permissions, isLoading: isPermissionsLoading } = usePermissions();
    const canEditAd = (permissions?.isAdmin || permissions?.isClient) && !permissions?.isPosOwner;
    const { data, isPending, error } = useGetOne('ad', { id: elementId }, { enabled: Boolean(elementId) && canEditAd });
    const { imageUrl, isLoading: isImageLoading } = useCampaignTileImage(elementId, data?.updatedAt);

    const isFormReady = Boolean(data) && !isPending && !isImageLoading;
    const formRecord = useMemo(() => mapAdvertisementToFormValues(data, imageUrl), [data, imageUrl]);
    const validateForm = useCallback(values => validateAdvertisementForm(values, translate, { requireImage: true }), [translate]);

    if (isPermissionsLoading || !canEditAd) {
        return null;
    }

    return (
        <StyledAdvertisementEdit>
            <Box>
                <Box className='popup-wrapper popup-wrapper-top border-bottom'>
                    <Box className='popup-box popup-box--header'>
                        <Typography variant='h4'>{translate('resources.adsCampaign.labels.editAdvertisement')}</Typography>
                        <Box>
                            <CustomCloseButton onClose={handleClose} />
                        </Box>
                    </Box>
                </Box>
                {!isFormReady ? (
                    <Box className='popup-wrapper' sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
                        {error ? (
                            <Typography color='error'>
                                {translate('common.errors.loadingData')}: {error.message}
                            </Typography>
                        ) : (
                            <CircularProgress />
                        )}
                    </Box>
                ) : (
                    <Edit
                        id={elementId}
                        resource='ad'
                        actions={false}
                        mutationMode='pessimistic'
                        redirect={false}
                        transform={transformAdvertisement}
                        mutationOptions={{
                            onSuccess: () => {
                                notify(translate('resources.adsCampaign.labels.successUpdated'), { type: 'success', multiLine: true });
                                queryClient.invalidateQueries({ queryKey: ['ad'] });
                                refresh();
                                handleClose();
                            },
                        }}
                    >
                        <Box className='popup-wrapper'>
                            <SimpleForm
                                key={`${elementId}-${imageUrl || 'no-image'}`}
                                mode='onSubmit'
                                reValidateMode='onSubmit'
                                record={formRecord}
                                toolbar={
                                    <TagCategoryToolbar onCancel={handleClose} cancelLabel={translate('common.cancel')} saveLabel={translate('common.save')} />
                                }
                                validate={validateForm}
                            >
                                <AdvertisementExistingImageSync imageUrl={imageUrl} />
                                <AdvertisementFormContent />
                            </SimpleForm>
                        </Box>
                    </Edit>
                )}
            </Box>
        </StyledAdvertisementEdit>
    );
};

AdvertisementEdit.propTypes = {
    handleClose: PropTypes.func,
    elementId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
